import flatten from 'lodash/flatten';
import router, { LOGIN_ROUTE, getReturnToQueryString } from '@/router';
import store from '@/store';
import UserService from '@/services/user';
import AuthenticationService from '@/services/authentication';
import ApplicationDataService from '@/services/applicationDataService';

function tryAccessAnonymously(to, next) {
  if (!to.meta.allowAnonymous) {
    next({
      ...LOGIN_ROUTE,
      query: getReturnToQueryString(to),
    });
  } else {
    next();
  }
}

async function tryRestoreApplicationData() {
  if (!store.getters.appConfigurationLoaded && !store.getters.apiUnreachable) {
    await ApplicationDataService.requestConfiguration();
  }
}

function hasNotCoveredPending(existingAccessPending, allowAccessPending) {
  return existingAccessPending
    .some(accessPendingToCheck => !allowAccessPending.includes(accessPendingToCheck));
}

function checkAccessPendingAndContinue(to, next) {
  if (store.getters.hasAnyAccessPending && to.name !== LOGIN_ROUTE.name) {
    const allowAccessPending = to.meta && flatten([to.meta.allowAccessPending]);
    const existingAccessPending = store.getters.accessPending;
    if (hasNotCoveredPending(existingAccessPending, allowAccessPending)) {
      AuthenticationService.logout();
    }
  }
  next();
}

function tryRestoreUserDataAndContinue(to, next) {
  if (!store.getters.user) {
    UserService.requestUserData().then(() => {
      next();
    }, () => {
      store.dispatch('setToken', null);
      tryAccessAnonymously(to, next);
    });
  } else {
    checkAccessPendingAndContinue(to, next);
  }
}

function checkPermissions(requiredPermissions, requiredAnyPermissions) {
  if (requiredPermissions && requiredPermissions.length > 0) {
    return UserService.hasPermission(...requiredPermissions);
  }

  if (requiredAnyPermissions && requiredAnyPermissions.length > 0) {
    return UserService.hasAnyPermission(...requiredAnyPermissions);
  }

  return true;
}

/**
 * Controla o acesso de usuários logados e rotas com acesso anônimo permitido.
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
function handleAuthentication(to, from, next) {
  tryRestoreApplicationData()
    .then(() => {
      if (!store.getters.authenticated) {
        tryAccessAnonymously(to, next);
      } else {
        tryRestoreUserDataAndContinue(to, next);
      }
    })
    .catch(() => {
      store.dispatch('setToken', null);
      next(LOGIN_ROUTE);
    });
  // Caso não seja possível recuperar os parâmetos do sistema, então um erro será emitido a partir
  // do interceptador de erros do Http.
}

/**
 * Controla a autorização de um usuário em acessar ou não uma rota, com base nas permissões.
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
function handleAutorization(to, from, next) {
  if (checkPermissions(to.meta.permissions, to.meta.anyPermissions)) {
    next();
    return;
  }
  const nextParams = to.name === LOGIN_ROUTE.name ? {} : LOGIN_ROUTE;
  next(nextParams);
}

export default {
  install() {
    store.dispatch('recoverToken', store);
    // Verifica se o usuário pode acessar o sistema (logado ou acesso anônimo)
    router.beforeEach(handleAuthentication);
    // Verifica se o usuário atual possui permissão para acessar uma rota específica.
    router.beforeEach(handleAutorization);
  },
};
